import React, { useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Row, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";
import * as moment from "moment";

import useChartColors from "Components/Common/useChartColors";
import { ActivityReportData } from "./types";

const CHART_ID = "activity-report-searches";

/** All three panels share one height so the card never grows to the longest
 * table and leaves the chart floating in white space. */
const PANEL_HEIGHT = 260;

/** Leaderboards are top-N lists; more rows than this belong in Activity Logs. */
const MAX_ROWS = 6;

const formatBucket = (bucket: string, granularity: string) => {
  if (granularity === "year") return bucket;

  const date =
    granularity === "month"
      ? new Date(`${bucket}-01T00:00:00Z`)
      : new Date(`${bucket}T00:00:00Z`);

  if (isNaN(date.getTime())) return bucket;

  return granularity === "month"
    ? date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
};

const Delta = ({
  value,
  comparable,
}: {
  value: number;
  comparable: boolean;
}) => {
  if (!comparable) return null;

  const flat = value === 0;
  const up = value > 0;
  const tone = flat ? "text-muted" : up ? "text-success" : "text-danger";
  const icon = flat
    ? "ri-subtract-line"
    : up
    ? "ri-arrow-right-up-line"
    : "ri-arrow-right-down-line";

  return (
    <span className={`${tone} fs-11`}>
      <i className={`${icon} align-middle me-1`}></i>
      {Math.abs(value)}%
    </span>
  );
};

const StatCell = ({
  label,
  value,
  decimals,
  change,
  comparable,
  className,
}: {
  label: string;
  value: number;
  decimals?: number;
  change: number;
  comparable: boolean;
  className?: string;
}) => (
  <Col xs={6} sm={3}>
    <div className={`p-3 border border-dashed ${className || ""}`}>
      <h5 className="mb-1">
        <CountUp start={0} end={value} decimals={decimals || 0} duration={2} />
      </h5>
      <p className="text-muted mb-1 fs-12">{label}</p>
      <Delta value={change} comparable={comparable} />
    </div>
  </Col>
);

const PanelHeading = ({ children }: { children: React.ReactNode }) => (
  <h6
    className="text-muted text-uppercase fs-11 mb-2"
    style={{ lineHeight: 1.6, letterSpacing: ".03em" }}
  >
    {children}
  </h6>
);

/**
 * A table panel fixed to the chart's height, scrolling internally rather than
 * stretching the row it sits in.
 *
 * Deliberately not `.table-card`: that class carries a -16px margin on every
 * side so a table can bleed to the edges of a card body. Here the table sits
 * under a heading and beside a sibling column, where those margins pull the
 * header band up over the heading and out into the gutter.
 */
const Panel = ({ children }: { children: React.ReactNode }) => (
  <div
    className="table-responsive border rounded"
    style={{ height: PANEL_HEIGHT, overflowY: "auto" }}
  >
    {children}
  </div>
);

/** Header cells stay put while the rows scroll under them, which needs an
 * opaque background of their own. */
const stickyHeadCell: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "var(--vz-light)",
  whiteSpace: "nowrap",
};

const EmptyRow = ({ children }: { children: React.ReactNode }) => (
  <tr>
    <td colSpan={2} className="text-center text-muted py-4 fs-13">
      {children}
    </td>
  </tr>
);

/**
 * Due diligence searches, which are recorded as their own reports rather than
 * as page views. The date, agency and user filters apply; the page filter
 * cannot, since a search is not a page.
 */
const DueDiligenceSearches = ({
  data,
  granularity,
  comparable,
  pageFiltered,
  loading,
}: {
  data: ActivityReportData | null;
  granularity: string;
  comparable: boolean;
  pageFiltered: boolean;
  loading: boolean;
}) => {
  const chartColors = useChartColors(CHART_ID);

  const searches = data?.searches;
  const points = useMemo(() => searches?.points || [], [searches]);

  const categories = useMemo(
    () => points.map((point) => formatBucket(point.date, granularity)),
    [points, granularity]
  );

  const series = useMemo(
    () => [{ name: "Searches", data: points.map((point) => point.searches) }],
    [points]
  );

  const peak = useMemo(
    () => points.reduce((max, point) => Math.max(max, point.searches), 0),
    [points]
  );

  const topSearchers = useMemo(
    () => (searches?.topSearchers || []).slice(0, MAX_ROWS),
    [searches]
  );
  const topTerms = useMemo(
    () => (searches?.topTerms || []).slice(0, MAX_ROWS),
    [searches]
  );

  const options: any = {
    chart: {
      height: PANEL_HEIGHT,
      type: "area",
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    colors: chartColors?.length ? chartColors : undefined,
    dataLabels: { enabled: false },
    stroke: { width: 2, curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.03 },
    },
    markers: { size: 0, hover: { sizeOffset: 4 } },
    xaxis: {
      categories,
      tickAmount: Math.min(Math.max(categories.length - 1, 1), 8),
      labels: { rotate: -45, rotateAlways: false, hideOverlappingLabels: true },
      tooltip: { enabled: false },
    },
    // Searches are whole numbers: without capping the ticks Apex repeats the
    // same rounded label on every gridline of a short axis.
    yaxis: {
      min: 0,
      forceNiceScale: peak > 0,
      tickAmount: Math.min(Math.max(peak, 1), 4),
      decimalsInFloat: 0,
      labels: { formatter: (value: number) => String(Math.round(value)) },
    },
    grid: { borderColor: "#f1f1f1", padding: { left: 8, right: 8 } },
  };

  const current = searches?.current;
  const change = searches?.change;

  return (
    <Col xxl={12}>
      <Card className="card-height-100">
        <CardHeader className="align-items-center d-flex flex-wrap gap-2">
          <div className="flex-grow-1">
            <h4 className="card-title mb-0">Due Diligence Searches</h4>
            {pageFiltered ? (
              <small className="text-muted">
                Searches are not narrowed by the page filter.
              </small>
            ) : null}
          </div>
          {!loading && searches?.lastSearchAt ? (
            <span className="text-muted fs-12">
              Last search {moment(new Date(searches.lastSearchAt)).fromNow()}
            </span>
          ) : null}
          {!loading && searches ? (
            <span className="badge bg-info-subtle text-info fs-12">
              {searches.searcherShare}% of active users searched
            </span>
          ) : null}
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <StatCell
              label="Searches"
              value={current?.total || 0}
              change={change?.total || 0}
              comparable={comparable}
              className="border-start-0"
            />
            <StatCell
              label="Searchers"
              value={current?.searchers || 0}
              change={change?.searchers || 0}
              comparable={comparable}
              className="border-start-0"
            />
            <StatCell
              label="Searches per Searcher"
              value={current?.searchesPerSearcher || 0}
              decimals={1}
              change={change?.searchesPerSearcher || 0}
              comparable={comparable}
              className="border-start-0"
            />
            <StatCell
              label="Bulk Search Reports"
              value={current?.bulkSearches || 0}
              change={change?.bulkSearches || 0}
              comparable={comparable}
              className="border-start-0 border-end-0"
            />
          </Row>
        </CardHeader>

        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : (current?.total || 0) === 0 ? (
            <div className="text-center text-muted py-5">
              No due diligence searches match the current filters.
            </div>
          ) : (
            <Row className="g-4">
              <Col xl={6}>
                <PanelHeading>Searches over time</PanelHeading>
                {peak === 0 ? (
                  <div
                    className="d-flex align-items-center justify-content-center text-muted fs-13 border border-dashed rounded"
                    style={{ height: PANEL_HEIGHT }}
                  >
                    No searches fall inside this period.
                  </div>
                ) : (
                  <div style={{ height: PANEL_HEIGHT }}>
                    <ReactApexChart
                      dir="ltr"
                      options={options}
                      series={series}
                      id={CHART_ID}
                      type="area"
                      height={PANEL_HEIGHT}
                      data-colors='["--vz-info"]'
                      className="apex-charts"
                    />
                  </div>
                )}
              </Col>

              <Col xl={3} md={6}>
                <PanelHeading>Top searchers</PanelHeading>
                <Panel>
                  <table className="table table-sm align-middle table-borderless mb-0">
                    <thead className="text-muted">
                      <tr>
                        <th scope="col" style={stickyHeadCell}>
                          User
                        </th>
                        <th
                          scope="col"
                          className="text-end"
                          style={stickyHeadCell}
                        >
                          Searches
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSearchers.length === 0 ? (
                        <EmptyRow>No searches carry a user.</EmptyRow>
                      ) : (
                        topSearchers.map((row) => (
                          <tr key={row.userId}>
                            <td>
                              <div
                                className="fw-medium text-truncate"
                                title={row.name}
                                style={{ maxWidth: "160px" }}
                              >
                                {row.name}
                              </div>
                              {row.agencyName ? (
                                <small className="text-muted">
                                  {row.agencyName}
                                </small>
                              ) : null}
                            </td>
                            <td className="text-end fw-semibold">
                              {row.searches}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Panel>
              </Col>

              <Col xl={3} md={6}>
                <PanelHeading>Most searched terms</PanelHeading>
                <Panel>
                  <table className="table table-sm align-middle table-borderless mb-0">
                    <thead className="text-muted">
                      <tr>
                        <th scope="col" style={stickyHeadCell}>
                          Term
                        </th>
                        <th
                          scope="col"
                          className="text-end"
                          style={stickyHeadCell}
                        >
                          Searches
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topTerms.length === 0 ? (
                        <EmptyRow>No search terms recorded.</EmptyRow>
                      ) : (
                        topTerms.map((row, index) => (
                          <tr key={`${row.term}-${index}`}>
                            <td>
                              {/* Company names run long; one line each keeps the
                                  rows level with the searchers beside them. */}
                              <div
                                className="fw-medium text-truncate"
                                title={row.term}
                                style={{ maxWidth: "180px" }}
                              >
                                {row.term}
                              </div>
                            </td>
                            <td className="text-end fw-semibold">
                              {row.searches}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Panel>
              </Col>

              {current && current.unattributed > 0 ? (
                <Col xs={12}>
                  <p className="text-muted fs-12 mb-0">
                    {current.unattributed} of {current.total} searches were
                    recorded without a user, so they count towards the total but
                    not towards searchers or the tables above.
                  </p>
                </Col>
              ) : null}
            </Row>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default DueDiligenceSearches;
